import { brand, system } from '@workday/canvas-tokens-web'

// Canvas documents shape.md (8px) for "Inputs, Toast, Tooltip, Snackbar". Flag-OFF
// restores the legacy radius via the Layer 1 `--cnvs-sys-shape-md: 0.25rem` polyfill
// (the lg input is the one accepted outlier, 0.375rem → 0.25rem).
const fieldRadius = `var(${system.shape.md})`

const size = {
  lg: {
    borderRadius: fieldRadius
  },

  md: {
    borderRadius: fieldRadius
  },

  sm: {
    borderRadius: fieldRadius
  },

  xs: {
    borderRadius: fieldRadius
  }
}

const sizes = {
  lg: {
    field: size.lg,
    addon: size.lg
  },
  md: {
    field: size.md,
    addon: size.md
  },
  sm: {
    field: size.sm,
    addon: size.sm
  },
  xs: {
    field: size.xs,
    addon: size.xs
  }
}

function focusRing(color = 'transparent') {
  // If the color is transparent, we don't want to show the white ring
  const innerRing = `0px 0px 0px 2px ${color === 'transparent' ? 'transparent' : 'white'}`
  const outerRing = `0px 0px 0px 4px ${color}`
  return `${innerRing}, ${outerRing}`
}

const focusOutline = `var(${brand.common.focusOutline})`

// Chakra invokes variant functions with `props.theme`, so the `body` type atoms are read
// from the active theme (legacy flag-OFF, sanaRebrandTextStyles flag-ON) and spread inline
// to win Chakra's per-size `fontSize`. Flag-ON `body` already carries fg.default; the
// explicit `color` below stays as the flag-OFF fallback (legacy `body` is colorless).
const variantOutline = ({ theme }) => {
  return {
    field: {
      ...theme.textStyles.body,
      color: `var(${system.color.fg.default})`,
      bg: `var(${system.color.bg.default})`,
      borderWidth: '1px',
      borderColor: `var(${system.color.border.input.default})`,
      '&::-webkit-input-placeholder, &::placeholder': {
        color: `var(${system.color.fg.muted.default})`
      },
      '&[data-warned]:not([aria-invalid=true], [data-invalid])': {
        borderWidth: '2px',
        borderColor: `var(${brand.common.caution.inner})`
      },
      '&:not(:focus, [readonly], [disabled], [data-disabled], [aria-invalid], [data-warned]):hover': {
        borderColor: `var(${system.color.border.input.hover})`
      },

      _focusVisible: {
        zIndex: 1,
        borderColor: focusOutline,
        boxShadow: `0 0 0 1px ${focusOutline}`,
        _invalid: {
          borderWidth: '2px',
          borderColor: `var(${system.color.brand.border.critical})`,
          boxShadow: focusRing(focusOutline),
          _hover: {
            boxShadow: focusRing(focusOutline)
          }
        },
        '&[data-warned]:not([aria-invalid=true], [data-invalid])': {
          borderWidth: '2px',
          borderColor: `var(${brand.common.caution.inner})`,
          boxShadow: focusRing(focusOutline),
          _hover: {
            boxShadow: focusRing(focusOutline)
          }
        }
      },
      // Canvas @deprecated the dedicated border.input.disabled token and redirects it to
      // fg.disabled, so the disabled border rides fg.disabled too (polyfills to slate-400
      // flag-OFF — the same value the legacy disabled border resolved to).
      _disabled: {
        opacity: 1,
        color: `var(${system.color.fg.disabled})`,
        bg: `var(${system.color.bg.alt.softer})`,
        borderColor: `var(${system.color.fg.disabled})`,
        _hover: {
          borderColor: `var(${system.color.fg.disabled})`
        }
      },
      _readOnly: {
        backgroundColor: `var(${system.color.bg.alt.softer})`,
        _hover: {
          borderColor: `var(${system.color.border.input.default})`
        },
        _focusVisible: {
          borderColor: focusOutline,
          boxShadow: `0 0 0 1px ${focusOutline} !important`
        }
      },
      _invalid: {
        borderWidth: '2px',
        borderColor: `var(${system.color.brand.border.critical})`,
        boxShadow: 'none'
      }
    },
    addon: {
      border: '1px solid',
      borderColor: `var(${system.color.border.input.default})`,
      bg: `var(${system.color.bg.alt.soft})`
    },
    element: {
      '&:has(+ input[disabled])': {
        opacity: 1,
        color: `var(${system.color.fg.disabled})`
      }
    }
  }
}

export const Input = {
  sizes,
  variants: {
    outline: variantOutline
  },
  defaultProps: {
    size: 'md',
    variant: 'outline'
  }
}
